import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MaterialIcon from "../../elements/MaterialIcon.jsx";
import Modal from "../Modal.jsx";
import MealIdeaPanel from "../mealIdeas/MealIdeaPanel.jsx";
import GroceryItemEditor from "../groceryItems/GroceryItemEditor.jsx";
import ItemTags from "../groceryItems/ItemTags.jsx";
import { checkOffRow } from "../../../redux/actions/calendar.js";
import { ideasOf } from "./mealIdeasOfItem.js";
import { selectMealIdeasIndex } from "../../../redux/food/selectors.js";

/**
 * What BoughtListItems needs from the store: meal ideas to name an item's idea, and putting an item
 * back on the active list, which is an untick op and works offline. Loading the meal ideas is left
 * to the caller.
 */
export function useBoughtListItemActions() {
	const dispatch = useDispatch();
	const mealIdeasById = useSelector((state) => selectMealIdeasIndex(state).ideasById);

	return {
		mealIdeasById,
		onPutBack: (item) => dispatch(checkOffRow(item.id, false)),
	};
}

/** `text` with each case-insensitive occurrence of `query` wrapped in a mark. */
function Highlighted({ text, query }) {
	const needle = query.trim().toLowerCase();
	if(!needle) {
		return text;
	}
	const parts = [];
	const haystack = text.toLowerCase();
	let from = 0;
	let at = haystack.indexOf(needle);
	while(at !== -1) {
		parts.push(text.slice(from, at));
		parts.push(<mark key={at}>{text.slice(at, at + needle.length)}</mark>);
		from = at + needle.length;
		at = haystack.indexOf(needle, from);
	}
	parts.push(text.slice(from));
	return parts;
}

/**
 * The items of a bought list, each with the meal ideas it came from and a button that puts it back on
 * the active list. Matches of `query` are highlighted.
 *
 * An item's meal idea opens for editing in a modal over the list, so closing it lands back where it
 * was opened from. So does the item itself, tapped by name, in the item editor. Given `onOpenIdea`
 * and `onOpenItem`, the idea and the item go to those instead, for the owner to open in place of the
 * list.
 */
export default function BoughtListItems({
	                                        items,
	                                        query = '',
	                                        mealIdeasById = {},
	                                        onPutBack,
	                                        onOpenItem = null,
	                                        onOpenIdea = null,
                                        }) {
	// Items put back and waiting on the server, so the button can't be pressed twice.
	const [pendingIds, setPendingIds] = useState(() => new Set());
	// { id, origin }: the meal idea open for editing, and the link it was opened from.
	const [openIdea, setOpenIdea] = useState(null);
	const idea = openIdea ? mealIdeasById[openIdea.id] || null : null;
	const closeIdea = () => setOpenIdea(null);
	// { item, origin }: the item open in the item editor, and the name it was opened from.
	const [openItem, setOpenItem] = useState(null);
	const closeItem = () => setOpenItem(null);

	const putBack = (item) => {
		setPendingIds((previous) => new Set(previous).add(item.id));
		const settle = () => setPendingIds((previous) => {
			const next = new Set(previous);
			next.delete(item.id);
			return next;
		});
		Promise.resolve(onPutBack(item)).then(settle, settle);
	};

	return (
		<>
		<ul className='bought_list'>
			{items.map((item) => {
				const mealIdeas = ideasOf(item, mealIdeasById);
				const pending = pendingIds.has(item.id);
				return (
					<li key={item.id || item.instanceNumber} className={`bought_list__item${pending ? ' is-pending' : ''}`}>
						<MaterialIcon className='bought_list__check'>check</MaterialIcon>
						<span className='bought_list__text'>
							<button
								type='button'
								className='btn-flat bought_list__name'
								title={`About ${item.name}`}
								onClick={(event) => (onOpenItem ? onOpenItem(item) : setOpenItem({ item, origin: event.currentTarget }))}
							>
								<Highlighted text={item.name} query={query} />
								{!!item.quantity &&
								<span className='bought_list__quantity'>{item.quantity}</span>
								}
							</button>
							{!!item.notes &&
							<span className='bought_list__notes'>{item.notes}</span>
							}
							{mealIdeas.length > 0 &&
							<span className='bought_list__ideas'>
								{mealIdeas.map((mealIdea, index) =>
								<React.Fragment key={mealIdea.id}>
									{index > 0 && ', '}
									<a
										href='#'
										className='bought_list__idea'
										onClick={(event) => {
											event.preventDefault();
											if(onOpenIdea) {
												onOpenIdea(mealIdea);
											} else {
												setOpenIdea({ id: mealIdea.id, origin: event.currentTarget });
											}
										}}
									>
										{mealIdea.name}
									</a>
								</React.Fragment>
								)}
							</span>
							}
							<ItemTags tags={item.tags} />
						</span>
						<button
							type='button'
							className='btn-flat bought_list__put-back'
							title='Put back on the grocery list'
							aria-label={`Put ${item.name} back on the grocery list`}
							disabled={pending}
							onClick={() => putBack(item)}
						>
							<MaterialIcon>undo</MaterialIcon>
						</button>
					</li>
				);
			})}
		</ul>
		{!!idea &&
		<Modal className='meal_idea_modal' origin={openIdea.origin} onClose={closeIdea}>
			<MealIdeaPanel key={idea.id} idea={idea} onClose={closeIdea} />
		</Modal>
		}
		{!!openItem &&
		<Modal className='meal_idea_modal grocery_item_modal' origin={openItem.origin} onClose={closeItem}>
			<GroceryItemEditor row={openItem.item} onClose={closeItem} />
		</Modal>
		}
		</>
	);
}
